import './OrdersListPage.scss';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Inbox } from 'lucide-react';
import { useLang } from '../../i18n';
import { get } from '../../services/api';
import Tabs from '../../components/Tabs';
import Card from '../../components/Card';
import Input from '../../components/Input';
import Button from '../../components/Button';
import OrderStatusBadge from '../../components/OrderStatusBadge';
import OrderProgressBar from '../../components/OrderProgressBar';
import EmptyState from '../../components/EmptyState';
import Pagination from '../../components/Pagination';
import Skeleton from '../../components/Skeleton';
import type { Order, Paginated } from '../../types';

type TabKey = 'all' | 'active' | 'completed' | 'cancelled';

const PAGE_SIZE = 12;

function progress(o: Order): number {
  if (!o.stages || o.stages.length === 0) return 0;
  const done = o.stages.filter((s) => s.status === 'done').length;
  return Math.round((done / o.stages.length) * 100);
}

function statusForTab(tab: TabKey): string {
  if (tab === 'active') return 'in_progress';
  if (tab === 'completed') return 'completed';
  if (tab === 'cancelled') return 'cancelled';
  return '';
}

export default function OrdersListPage() {
  const { t } = useLang();
  const [tab, setTab] = useState<TabKey>('all');
  const [query, setQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<Order[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const tabs = useMemo(() => ([
    { value: 'all', label: t('orders.tab.all') },
    { value: 'active', label: t('orders.tab.active') },
    { value: 'completed', label: t('orders.tab.completed') },
    { value: 'cancelled', label: t('orders.tab.cancelled') },
  ]), [t]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('page_size', String(PAGE_SIZE));
      const s = statusForTab(tab);
      if (s) params.set('status', s);
      if (query) params.set('search', query);
      if (dateFrom) params.set('start_at_after', dateFrom);
      if (dateTo) params.set('start_at_before', dateTo);
      try {
        const res = await get<Paginated<Order>>(`/me/orders/?${params.toString()}`);
        if (cancelled) return;
        setItems(res.results || []);
        setTotal(res.count || 0);
      } catch {
        if (!cancelled) { setItems([]); setTotal(0); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tab, query, dateFrom, dateTo, page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="orders-list">
      <header className="orders-list__head">
        <h1 className="orders-list__title">{t('orders.title')}</h1>
      </header>

      <Tabs
        items={tabs}
        value={tab}
        onChange={(v) => { setTab(v as TabKey); setPage(1); }}
        ariaLabel={t('orders.title')}
      />

      <Card padding="md" className="orders-list__filters">
        <div className="orders-list__filter orders-list__filter--grow">
          <Input
            placeholder={t('orders.search')}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
            iconLeft={<Search size={16} />}
            aria-label={t('orders.search')}
          />
        </div>
        <div className="orders-list__filter">
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
            aria-label={t('orders.date_from')}
          />
        </div>
        <div className="orders-list__filter">
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
            aria-label={t('orders.date_to')}
          />
        </div>
      </Card>

      {loading ? (
        <div className="orders-list__items">
          <Skeleton height={100} />
          <Skeleton height={100} />
          <Skeleton height={100} />
        </div>
      ) : items.length === 0 ? (
        <Card padding="lg">
          <EmptyState
            icon={<Inbox size={48} />}
            title={t('orders.empty.title')}
            description={t('orders.empty.description')}
            cta={<Link to="/contacts"><Button variant="primary" size="md">{t('orders.empty.cta')}</Button></Link>}
          />
        </Card>
      ) : (
        <>
          <div className="orders-list__items">
            {items.map((o) => (
              <Link key={o.public_id} to={`/cabinet/orders/${o.public_id}`} className="orders-list__item-link">
                <Card padding="md" className="orders-list__item">
                  <div className="orders-list__item-head">
                    <div className="orders-list__item-info">
                      <div className="orders-list__item-id">{o.human_id}</div>
                      <div className="orders-list__item-title">{o.title}</div>
                    </div>
                    <OrderStatusBadge status={o.status} />
                  </div>
                  <OrderProgressBar value={progress(o)} max={100} showValue />
                  <div className="orders-list__item-meta">
                    <span>{o.start_at ? new Date(o.start_at).toLocaleDateString() : '—'}</span>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
          {totalPages > 1 ? (
            <Pagination page={page} total={totalPages} onChange={setPage} />
          ) : null}
        </>
      )}
    </div>
  );
}
